import { useEffect, useMemo, useState } from "react";
import { Popup } from "components/popups/Popup";
import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";

type MaintenancePopupProps = {
  isOpen: boolean;
  isSaving?: boolean;
  onClose: () => void;
  onConfirm: (payload: { dateStart: string; dateEnd: string }) => void;
};

const getChangedValue = (input: unknown): string => {
  if (typeof input === "string" || typeof input === "number") return String(input);
  if (input && typeof input === "object" && "target" in input) {
    const target = (input as { target?: { value?: unknown } }).target;
    const nextValue = target?.value;
    if (typeof nextValue === "string" || typeof nextValue === "number") {
      return String(nextValue);
    }
  }
  return "";
};

const MaintenancePopup: React.FC<MaintenancePopupProps> = ({
  isOpen,
  isSaving = false,
  onClose,
  onConfirm,
}) => {
  const [mode, setMode] = useState<"now" | "date">("now");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setMode("now");
    setDateStart("");
    setDateEnd("");
  }, [isOpen]);

  const isConfirmDisabled = useMemo(() => {
    if (mode !== "date") return false;
    if (!dateStart || !dateEnd) return true;
    return dateEnd < dateStart;
  }, [dateEnd, dateStart, mode]);

  if (!isOpen) return null;

  return (
    <Popup
      title="Maintenance"
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick={false}
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              primary
              onClick={() => {
                onConfirm({
                  dateStart: mode === "date" ? dateStart : "",
                  dateEnd: mode === "date" ? dateEnd : "",
                });
              }}
              disabled={isSaving || isConfirmDisabled}
            >
              Ok
            </Button>
          </group>
        ),
      }}
    >
      <group data-direction="column" data-gap="15" data-space="20" data-height="autofit">
        <group data-gap="10">
          <Input
            type={InputType.Radio}
            name="maintenance_mode"
            label="Now"
            value={mode}
            {...({ radioValue: "now" } as any)}
            onChange={(event) => setMode((event.target as HTMLInputElement).value as "now" | "date")}
            disabled={isSaving}
          />
          <Input
            type={InputType.Radio}
            name="maintenance_mode"
            label="Date Range"
            value={mode}
            {...({ radioValue: "date" } as any)}
            onChange={(event) => setMode((event.target as HTMLInputElement).value as "now" | "date")}
            disabled={isSaving}
          />
        </group>

        <group data-gap="10" data-align="center" data-wrap="no">
          <Input
            type="datepicker"
            name="maintenance_date_start"
            label="Start date"
            value={dateStart}
            placeholder="Start date"
            onChange={(value) => setDateStart(getChangedValue(value))}
            disabled={mode !== "date" || isSaving}
          />
          <Input
            type="datepicker"
            name="maintenance_date_end"
            label="End date"
            value={dateEnd}
            min={dateStart || undefined}
            placeholder="End date"
            onChange={(value) => setDateEnd(getChangedValue(value))}
            disabled={mode !== "date" || isSaving}
          />
        </group>
      </group>
    </Popup>
  );
};

export default MaintenancePopup;
